/**
 * An implementation of the RDF Dataset Normalization specification.
 * This library works in the browser and node.js.
 *
 * BSD 3-Clause License
 * Copyright (c) 2016-2023 Digital Bazaar, Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * Redistributions of source code must retain the above copyright notice,
 * this list of conditions and the following disclaimer.
 *
 * Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * Neither the name of the Digital Bazaar, Inc. nor the names of its
 * contributors may be used to endorse or promote products derived from
 * this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS
 * IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED
 * TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
 * PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
 * HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED
 * TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 * PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
 * NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 * SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
'use strict';

const RDFC10 = require('./RDFC10');
const RDFC10Sync = require('./RDFC10Sync');

// return a dataset from input dataset or n-quads
function _inputToDataset(input, options) {
  if(options.inputFormat) {
    if(options.inputFormat === 'application/n-quads') {
      if(typeof input !== 'string') {
        throw new Error('N-Quads input must be a string.');
      }
      return exports.NQuads.parse(input);
    }
    throw new Error(
      `Unknown canonicalization input format: "${options.inputFormat}".`);
  }
  return input;
}

// check for valid output format
function _checkOutputFormat(options) {
  // only N-Quads supported
  if(options.format) {
    if(options.format !== 'application/n-quads') {
      throw new Error(
        `Unknown canonicalization output format: "${options.format}".`);
    }
  }
}

// helper to trace URDNA2015 usage
function _traceURDNA2015() {
  if(!!globalThis.RDF_CANONIZE_TRACE_URDNA2015) {
    console.trace('[rdf-canonize] URDNA2015 is deprecated, use RDFC-1.0');
  }
}

// expose helpers
exports.NQuads = require('./NQuads');
exports.IdentifierIssuer = require('./IdentifierIssuer');

/**
 * Asynchronously canonizes an RDF dataset.
 *
 * @param {Array|object|string} input - The input to canonize given as a
 *   dataset or format specified by 'inputFormat' option.
 * @param {object} options - The options to use:
 *   {string} algorithm - The canonicalization algorithm to use, `RDFC-1.0`.
 *   {Function} [createMessageDigest] - A factory function for creating a
 *     `MessageDigest` interface that overrides the built-in message digest
 *     implementation used by the canonize algorithm; note that using a hash
 *     algorithm (or HMAC algorithm) that differs from the one specified by
 *     the canonize algorithm will result in different output.
 *   {string} [messageDigestAlgorithm=sha256] - Message digest algorithm used
 *     by the default implementation of `createMessageDigest`. Supported
 *     algorithms are: 'sha256', 'sha384', 'sha512', and the 'SHA###' and
 *     'SHA-###' variations.
 *   {Map} [canonicalIdMap] - An optional Map to be populated by the canonical
 *     identifier issuer with the bnode identifier mapping generated by the
 *     canonicalization algorithm.
 *   {string} [inputFormat] - The format of the input. Use
 *     'application/n-quads' for a N-Quads string that will be parsed. Omit or
 *     falsy for a JSON dataset.
 *   {string} [format] - The format of the output. Omit or use
 *     'application/n-quads' for a N-Quads string.
 *   {number} [maxWorkFactor=1] - Control of the maximum number of times to run
 *     deep comparison algorithms (such as the N-Degree Hash Quads algorithm
 *     used in RDFC-1.0) before bailing out and throwing an error; this is a
 *     useful setting for preventing wasted CPU cycles or DoS when canonizing
 *     meaningless or potentially malicious datasets. This parameter sets the
 *     maximum number of iterations based on the number of non-unique blank
 *     nodes. `0` to disable iterations, `1` for a O(n) limit, `2` for a O(n^2)
 *     limit, `3` and higher may handle "poison" graphs but may take
 *     significant computational resources, `Infinity` for no limitation.
 *     Defaults to `1` which can handle many common inputs.
 *   {number} [maxDeepIterations=-1] - The maximum number of times to run
 *     deep comparison algorithms (such as the N-Degree Hash Quads algorithm
 *     used in RDFC-1.0) before bailing out and throwing an error; this is a
 *     useful setting for preventing wasted CPU cycles or DoS when canonizing
 *     meaningless or potentially malicious datasets. If set to a value other
 *     than `-1` it will explicitly set the number of iterations and override
 *     `maxWorkFactor`. It is recommended to use `maxWorkFactor`.
 *   {AbortSignal} [signal] - An AbortSignal used to abort the operation. The
 *     aborted status is only periodically checked for performance reasons.
 *   {boolean} [rejectURDNA2015=false] - Reject the "URDNA2015" algorithm name
 *     instead of treating it as an alias for "RDFC-1.0".
 *
 * @returns {Promise<object>} - A Promise that resolves to the canonicalized
 *   RDF Dataset.
 */
exports.canonize = async function(input, options = {}) {
  const dataset = _inputToDataset(input, options);
  _checkOutputFormat(options);

  if(!('algorithm' in options)) {
    throw new Error('No RDF Dataset Canonicalization algorithm specified.');
  }
  if(options.algorithm === 'RDFC-1.0') {
    return new RDFC10(options).main(dataset);
  }
  // URDNA2015 deprecated, handled as alias for RDFC-1.0 if allowed
  if(options.algorithm === 'URDNA2015' && !options.rejectURDNA2015) {
    _traceURDNA2015();
    return new RDFC10(options).main(dataset);
  }
  throw new Error(
    'Invalid RDF Dataset Canonicalization algorithm: ' + options.algorithm);
};

/**
 * This method is no longer available in the public API, it is for testing
 * only. It synchronously canonizes an RDF dataset and does not work in the
 * browser.
 *
 * @param {Array|object|string} input - The input to canonize given as a
 *   dataset or format specified by 'inputFormat' option.
 * @param {object} options - The options to use:
 *   {string} algorithm - The canonicalization algorithm to use, `RDFC-1.0`.
 *   {Function} [createMessageDigest] - A factory function for creating a
 *     `MessageDigest` interface that overrides the built-in message digest
 *     implementation used by the canonize algorithm; note that using a hash
 *     algorithm (or HMAC algorithm) that differs from the one specified by
 *     the canonize algorithm will result in different output.
 *   {string} [messageDigestAlgorithm=sha256] - Message digest algorithm used
 *     by the default implementation of `createMessageDigest`. Supported
 *     algorithms are: 'sha256', 'sha384', 'sha512', and the 'SHA###' and
 *     'SHA-###' variations.
 *   {Map} [canonicalIdMap] - An optional Map to be populated by the canonical
 *     identifier issuer with the bnode identifier mapping generated by the
 *     canonicalization algorithm.
 *   {string} [inputFormat] - The format of the input. Use
 *     'application/n-quads' for a N-Quads string that will be parsed. Omit or
 *     falsy for a JSON dataset.
 *   {string} [format] - The format of the output. Omit or use
 *     'application/n-quads' for a N-Quads string.
 *   {number} [maxWorkFactor=1] - Control of the maximum number of times to run
 *     deep comparison algorithms (such as the N-Degree Hash Quads algorithm
 *     used in RDFC-1.0) before bailing out and throwing an error; this is a
 *     useful setting for preventing wasted CPU cycles or DoS when canonizing
 *     meaningless or potentially malicious datasets. This parameter sets the
 *     maximum number of iterations based on the number of non-unique blank
 *     nodes. `0` to disable iterations, `1` for a O(n) limit, `2` for a O(n^2)
 *     limit, `3` and higher may handle "poison" graphs but may take
 *     significant computational resources, `Infinity` for no limitation.
 *     Defaults to `1` which can handle many common inputs.
 *   {number} [maxDeepIterations=-1] - The maximum number of times to run
 *     deep comparison algorithms (such as the N-Degree Hash Quads algorithm
 *     used in RDFC-1.0) before bailing out and throwing an error; this is a
 *     useful setting for preventing wasted CPU cycles or DoS when canonizing
 *     meaningless or potentially malicious datasets. If set to a value other
 *     than `-1` it will explicitly set the number of iterations and override
 *     `maxWorkFactor`. It is recommended to use `maxWorkFactor`.
 *   {number} [timeout=1000] - The maximum number of milliseconds before the
 *     operation will timeout. This is only periodically checked for
 *     performance reasons. Use 0 to disable. Note: This is a replacement for
 *     the async canonize `signal` option common timeout use case. If complex
 *     abort logic is required, use the async function and the `signal`
 *     parameter.
 *   {boolean} [rejectURDNA2015=false] - Reject the "URDNA2015" algorithm name
 *     instead of treating it as an alias for "RDFC-1.0".
 *
 * @returns {Promise<object>} - A Promise that resolves to the canonicalized
 *   RDF Dataset.
 */
exports._canonizeSync = function(input, options = {}) {
  const dataset = _inputToDataset(input, options);
  _checkOutputFormat(options);

  if(!('algorithm' in options)) {
    throw new Error('No RDF Dataset Canonicalization algorithm specified.');
  }
  if(options.algorithm === 'RDFC-1.0') {
    return new RDFC10Sync(options).main(dataset);
  }
  // URDNA2015 deprecated, handled as alias for RDFC-1.0 if allowed
  if(options.algorithm === 'URDNA2015' && !options.rejectURDNA2015) {
    _traceURDNA2015();
    return new RDFC10Sync(options).main(dataset);
  }
  throw new Error(
    'Invalid RDF Dataset Canonicalization algorithm: ' + options.algorithm);
};
